import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, Search } from "lucide-react";
import { createPortal } from "react-dom";

export default function SelectMenu({
  label,
  options = [],
  value,
  onChange,
  searchable = false,
  icon: Icon,
  disabled = false,
  onContainerClick,
  usePortal = false, // ✅ optional prop for pages that need it
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState({});

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && usePortal && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuStyle({
        position: "absolute",
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
        zIndex: 9999,
      });
    }
  }, [isOpen, usePortal]);

  const filteredOptions = searchable
    ? options.filter((option) =>
      String(option.label ?? "").toLowerCase().includes(searchTerm.toLowerCase())
    )
    : options;


  const handleSelect = (optionValue) => {
    if (disabled) return;
    onChange(optionValue);
    setIsOpen(false);
  };

  const getDisplayValue = () => {
    if (value && value !== "") {
      return options.find((opt) => opt.value === value)?.label || value;
    }
    return `Select ${label}`;
  };

  const menuContent = (
    <div
      ref={dropdownRef}
      className="z-50 bg-white border rounded shadow-2xl max-h-64 overflow-hidden animate-in slide-in-from-top-2 duration-200"
      style={usePortal ? menuStyle : {}}
    >


      <div className="py-1 max-h-52 overflow-y-auto">
        {filteredOptions.length === 0 ? (
          <div className="px-4 py-3 text-sm text-center text-slate-400">
            No {label.toLowerCase()} available
          </div>
        ) : (
          filteredOptions.map((option) => (
            <div
              key={option.value}
              className={`px-4 py-3 cursor-pointer text-sm hover:bg-slate-50 flex items-center transition-colors ${value === option.value
                ? "bg-sidebarHover text-primary hover:bg-[rgba(255,48,48,0.16)] border-r-2 border-primary"
                : "text-slate-700"
                }`}
              onClick={() => handleSelect(option.value)}
            >
              <span className="font-medium">{option.label}</span>
            </div>
          ))
        )}
      </div>

    </div>
  );

  return (
    <div className="relative group" onClick={onContainerClick}>
      <div
        ref={buttonRef}
        className={`w-full px-2 sm:px-3 py-2 border-2 rounded bg-white flex items-center justify-between min-h-[36px] sm:min-h-[44px] transition-all duration-200 ${disabled
          ? "border-slate-200 bg-slate-50 cursor-not-allowed opacity-60"
          : isOpen
            ? "shadow-lg cursor-pointer"
            : "border-slate-200 hover:border-slate-300 hover:shadow-md cursor-pointer"
          }`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        {searchable && isOpen ? (
          <input
            type="text"
            autoFocus
            placeholder={`Search ${label.toLowerCase()}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full text-sm outline-none"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className={`text-sm ${disabled ? "text-slate-400" : "text-slate-700"} truncate mr-2`}>
            {getDisplayValue()}
          </span>
        )}
        <ChevronDown
          className={`w-4 h-4 transition-transform duration-200 ${disabled ? "text-slate-300" : "text-slate-400"
            } ${isOpen ? "rotate-180" : ""}`}
        />
      </div>


      {/* Render dropdown either inside or in portal */}
      {isOpen &&
        !disabled &&
        (usePortal
          ? createPortal(menuContent, document.body)
          : (
            <div className="absolute w-full mt-2">{menuContent}</div>
          ))}
    </div>
  );
}
